import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import API_BASE from '../apiConfig';
import { FaChartLine, FaExclamationCircle } from 'react-icons/fa';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

function SortTh({ label, col, sortKey, sortDir, onSort, align = 'left' }) {
  const active = sortKey === col;
  return (
    <th onClick={() => onSort(col)} style={{
      padding: '11px 14px', fontWeight: 800, fontSize: 10, textTransform: 'uppercase',
      letterSpacing: '0.08em', textAlign: align, color: active ? getBrandColors().yellow : undefined,
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontFamily: FONT,
    }}>
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10 }}>
        {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

const TournamentPLReport = ({ embedded = false, extTournamentId = '', extAgeCode = '' } = {}) => {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [ageCode, setAgeCode] = useState('');   // filter by age_code (from report rows)
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const [catSortKey, setCatSortKey] = useState('category');
  const [catSortDir, setCatSortDir] = useState('asc');
  const [stuSortKey, setStuSortKey] = useState('student_name');
  const [stuSortDir, setStuSortDir] = useState('asc');

  useEffect(() => { loadTournaments(); }, []);

  const loadTournaments = async () => {
    try {
      const res = await fetch(`${BASE}/tournament`);
      const data = await res.json();
      if (data.success) setTournaments(data.tournaments || []);
    } catch (err) { console.error(err); }
  };

  const loadReport = async (id) => {
    if (!id) { setReport(null); return; }
    setError('');
    try {
      const res = await fetch(`${BASE}/tournament/${id}/pl-report`);
      const data = await res.json();
      if (data.success) setReport(data.report);
      else { setReport(null); setError(data.message || 'Failed to load P&L report'); }
    } catch (err) { setError(err.message); }
  };

  const onTournamentChange = (id) => {
    setTournamentId(id);
    setAgeCode('');
    loadReport(id);
  };

  // Embedded (hub) mode: one coherent load on mount and on any shared-filter change.
  useEffect(() => {
    if (!embedded) return;
    setTournamentId(extTournamentId ? String(extTournamentId) : '');
    setAgeCode(extAgeCode || '');
    loadReport(extTournamentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, extTournamentId, extAgeCode]);

  // Age-category options come from the report itself (each carries category + age_code).
  const categoryOptions = report ? report.by_category.map(c => ({ code: c.age_code, name: c.category })) : [];

  const handleCatSort = (col) => {
    setCatSortDir(prev => (catSortKey === col ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setCatSortKey(col);
  };
  const handleStuSort = (col) => {
    setStuSortDir(prev => (stuSortKey === col ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setStuSortKey(col);
  };

  const sortedCategories = report ? [...report.by_category]
    .filter(c => !ageCode || c.age_code === ageCode)
    .sort((a, b) => {
      let av = a[catSortKey], bv = b[catSortKey];
      if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
      if (av < bv) return catSortDir === 'asc' ? -1 : 1;
      if (av > bv) return catSortDir === 'asc' ? 1 : -1;
      return 0;
    }) : [];

  const sortedStudents = report ? [...report.by_student]
    .filter(s => !ageCode || s.age_code === ageCode)
    .sort((a, b) => {
      let av = a[stuSortKey], bv = b[stuSortKey];
      if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
      if (av < bv) return stuSortDir === 'asc' ? -1 : 1;
      if (av > bv) return stuSortDir === 'asc' ? 1 : -1;
      return 0;
    }) : [];

  const glassCard = {
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: T.glassBdr, borderRadius: '16px', boxShadow: T.glassSh,
    padding: '24px', boxSizing: 'border-box', marginBottom: '20px',
  };
  const inputStyle = {
    border: `1.5px solid ${T.inputBdr}`, borderRadius: '10px', padding: '10px 14px',
    background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '14px',
    color: T.inputText, fontFamily: FONT,
  };
  const labelStyle = {
    display: 'block', marginBottom: '8px', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT,
  };
  const statCard = (label, value, color) => (
    <div style={{ flex: 1, padding: '16px 20px', background: T.glass, borderRadius: '12px', border: T.glassBdr }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: FONT, marginTop: 4 }}>${value.toFixed(2)}</div>
    </div>
  );

  return (
    <div style={embedded ? { padding: 0, boxSizing: 'border-box' } : { minHeight: '100dvh', padding: '20px 28px 48px', boxSizing: 'border-box' }}>
      {!embedded && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <PageHeaderIcon size={48} icon={<FaChartLine size={22} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>
            Tournament P&L Report
          </h1>
          <p style={{ fontSize: '13px', color: T.textMuted, margin: '4px 0 0', fontFamily: FONT }}>
            Overall, by age category, and by student
          </p>
        </div>
      </div>
      )}

      {error && (
        <div style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderRadius: '10px', marginBottom: '18px',
          fontSize: '13px', fontWeight: 600, fontFamily: FONT, background: 'rgba(248,113,113,0.12)', color: getBrandColors().red,
          border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={16} /><span>{error}</span>
        </div>
      )}

      {!embedded && (
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Tournament</label>
            <select value={tournamentId} onChange={(e) => onTournamentChange(e.target.value)} style={inputStyle}>
              <option value="">— Choose —</option>
              {tournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name} ({t.status})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age Category</label>
            <select value={ageCode} onChange={(e) => setAgeCode(e.target.value)} style={inputStyle} disabled={!report}>
              <option value="">All</option>
              {categoryOptions.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {report && (
        <>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            {statCard('Total Income', report.overall.income, getBrandColors().green)}
            {statCard('Total Expenses', report.overall.expenses, getBrandColors().red)}
            {statCard('Net Profit', report.overall.profit, report.overall.profit >= 0 ? getBrandColors().green : getBrandColors().red)}
          </div>

          <div style={glassCard}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 16px', fontFamily: FONT }}>
              By Age Category
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: FONT, color: T.textPrimary }}>
                <thead>
                  <tr style={{ background: T.glass, borderBottom: `2px solid ${T.inputBdr}` }}>
                    <SortTh label="Category" col="category" sortKey={catSortKey} sortDir={catSortDir} onSort={handleCatSort} />
                    <SortTh label="Registrations" col="registrations" sortKey={catSortKey} sortDir={catSortDir} onSort={handleCatSort} align="center" />
                    <SortTh label="Income" col="income" sortKey={catSortKey} sortDir={catSortDir} onSort={handleCatSort} align="right" />
                    <SortTh label="Expenses" col="expenses" sortKey={catSortKey} sortDir={catSortDir} onSort={handleCatSort} align="right" />
                    <SortTh label="Profit" col="profit" sortKey={catSortKey} sortDir={catSortDir} onSort={handleCatSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedCategories.map((c, idx) => (
                    <tr key={c.age_code} style={{ background: idx % 2 === 0 ? 'transparent' : T.glass, borderBottom: `1px solid ${T.inputBdr}` }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.category}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{c.registrations}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: getBrandColors().green }}>${c.income.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: getBrandColors().red }}>${c.expenses.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: c.profit >= 0 ? getBrandColors().green : getBrandColors().red }}>${c.profit.toFixed(2)}</td>
                    </tr>
                  ))}
                  {sortedCategories.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: T.textMuted }}>No data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={glassCard}>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: T.textPrimary, margin: '0 0 16px', fontFamily: FONT }}>
              By Student
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: FONT, color: T.textPrimary }}>
                <thead>
                  <tr style={{ background: T.glass, borderBottom: `2px solid ${T.inputBdr}` }}>
                    <SortTh label="Student" col="student_name" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} />
                    <SortTh label="Category" col="age_code" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} align="center" />
                    <SortTh label="Days" col="number_of_days_selected" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} align="center" />
                    <SortTh label="Total Cost" col="total_cost" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} align="right" />
                    <SortTh label="Refunded" col="refunded_amount" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} align="right" />
                    <SortTh label="Net Income" col="net_income" sortKey={stuSortKey} sortDir={stuSortDir} onSort={handleStuSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((s, idx) => (
                    <tr key={s.registration_id} style={{ background: idx % 2 === 0 ? 'transparent' : T.glass, borderBottom: `1px solid ${T.inputBdr}` }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{s.student_name}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{s.age_code}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>{s.number_of_days_selected}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>${s.total_cost.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: getBrandColors().red }}>${s.refunded_amount.toFixed(2)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: getBrandColors().green }}>${s.net_income.toFixed(2)}</td>
                    </tr>
                  ))}
                  {sortedStudents.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: T.textMuted }}>No paid registrations yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TournamentPLReport;
